import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import {
	getSocialMediaPlatformLogoUrl,
	isOrphanedConnection,
	isScheduledOrPendingOrDraftOrNew,
} from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { baseStyleSheet } from '../../../styles/styles';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { PopoverType, TinyPopover } from '../../TinyPopover';
import { CancelIconFill } from '../../svgs/icons/CancelIconFill';
import { SocialMediaPostTargetStatusIndicators } from '../SocialMediaPostTargetStatusIndicators';
import { SocialMediaPlatformTableType } from './models';
import { styleSheet } from './styles';

interface ISocialMediaPlatformRowProps extends IImpersonationContextComponentProps {
	connection: Api.ISocialMediaConnection;
	isRemovePending?: boolean;
	isRemovingTarget?: boolean;
	onRemoveOrphanedTargetClicked?(connection: Api.ISocialMediaConnection): void;
	onSelectedChanged(selected: boolean, connection: Api.ISocialMediaConnection): void;
	postSource?: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	postTarget?: Api.IPostTarget;
	selected: boolean;
	type: SocialMediaPlatformTableType;
}

const _SocialMediaPlatformRow: React.FC<ISocialMediaPlatformRowProps> = props => {
	const {
		connection,
		selected,
		isRemovePending,
		isRemovingTarget,
		onRemoveOrphanedTargetClicked,
		onSelectedChanged,
		type,
		postTarget,
		postSource,
	} = props;
	const [showDisconnectedPopover, , setDisconnectedPopover] = useLambda(false);
	const { logInput } = useEventLogging();
	const userSession = useUserSession();
	const { selectedCreator } = useEditSocialMediaPostContext();

	const onChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onSelectedChanged(e.target.checked, connection);
		},
		[connection, onSelectedChanged]
	);

	const onRemoveClicked = React.useCallback(() => {
		onRemoveOrphanedTargetClicked?.(connection);
		logInput('RemoveOrphanedPostTarget', 'Click');
	}, [connection, logInput, onRemoveOrphanedTargetClicked]);

	const onReconnectLinkClicked = React.useCallback(() => {
		const { origin, pathname } = window.location;
		window?.open(`${origin}${pathname}#/settings/social-media/social-media`, '_blank');
		logInput('ReconnectAttemptLink', 'Click');
	}, [logInput]);

	const scheduledOrPendingOrDraftOrNew = React.useMemo(() => {
		return isScheduledOrPendingOrDraftOrNew(postSource);
	}, [postSource]);

	if (!scheduledOrPendingOrDraftOrNew && !selected) {
		// return if post exists and is completed and connection is not selected
		return null;
	}

	// An orphaned connection's account was removed, so it can't be posted to. Surface it while the
	// post is still editable so the failing target is visible; keep hiding it on completed views.
	const orphanedConnection = isOrphanedConnection(connection);
	if (orphanedConnection && !scheduledOrPendingOrDraftOrNew) {
		return null;
	}

	const canRemoveOrphanedTarget =
		orphanedConnection &&
		!!onRemoveOrphanedTargetClicked &&
		type !== SocialMediaPlatformTableType.ReadOnly &&
		!!postSource?.canEdit;

	return connection ? (
		<li className={css(styleSheet.listContainer)}>
			<div className={css(styleSheet.names)}>
				{postTarget && !scheduledOrPendingOrDraftOrNew ? (
					<SocialMediaPostTargetStatusIndicators
						postStatus={postSource?.status}
						postTarget={postTarget}
						showTargetProvider={false}
					/>
				) : (
					<div
						className={css(
							styleSheet.checkboxContainer,
							selected && styleSheet.checkboxContainerSelected,
							(connection?.state === Api.SocialMediaConnectionState.Disconnected ||
								connection?.state === Api.SocialMediaConnectionState.NoTarget ||
								connection?.state === Api.SocialMediaConnectionState.Unknown) &&
								styleSheet.disconnected
						)}
					>
						<Checkbox
							backgroundFillColor='white'
							checked={selected}
							childrenPosition='right'
							disabled={
								type === SocialMediaPlatformTableType.ReadOnly ||
								(postSource && !postSource.canEdit) ||
								orphanedConnection
							}
							id={`${connection?.postTargetId} - ${connection?.userId} - ${connection?.type}`}
							onChange={onChange}
						/>
						<img className={css(styleSheet.iconRow)} src={getSocialMediaPlatformLogoUrl(connection?.type)} />
						<span className={css(styleSheet.nameAdjustment)}>{connection?.type}</span>
					</div>
				)}
			</div>
			<div className={css(styleSheet.accountLogin, styleSheet.tableRowElement, baseStyleSheet.flex)}>
				{orphanedConnection ? (
					<>
						<p>This account is no longer connected and will be skipped.</p>
						{canRemoveOrphanedTarget ? (
							<Button
								className={css(styleSheet.removeTargetButton)}
								disabled={isRemovePending}
								isLoading={isRemovingTarget}
								kind='destructive'
								onClick={onRemoveClicked}
								size='small'
							>
								<span>Remove</span>
							</Button>
						) : null}
					</>
				) : (
					<>
						<p>{connection?.userName}</p>
						{connection?.state !== Api.SocialMediaConnectionState.Connected && scheduledOrPendingOrDraftOrNew && (
							<>
								<TinyPopover
									align='end'
									anchor={
										<div
											onMouseEnter={setDisconnectedPopover(true)}
											onMouseLeave={setDisconnectedPopover(false)}
											className={css(styleSheet.cancelMargin)}
										>
											<CancelIconFill />
										</div>
									}
									styles={[styleSheet.popoverMargin]}
									dismissOnOutsideAction={true}
									isOpen={showDisconnectedPopover}
									placement={['bottom', 'right', 'top', 'left']}
									type={PopoverType.errorSecondary}
								>
									<p className={css(styleSheet.popover)}>Disconnected </p>
								</TinyPopover>

								{selected && !props.impersonationContext && selectedCreator.id === userSession.user.id ? (
									<button
										className={css(baseStyleSheet.ctaButtonSmall, styleSheet.reconnectButton)}
										onClick={onReconnectLinkClicked}
									>
										<span>Reconnect</span>
									</button>
								) : null}
							</>
						)}
					</>
				)}
			</div>
			<p className={css(styleSheet.postLocation, styleSheet.tableRowElement)}>
				{orphanedConnection ? '' : `@${connection?.postTargetDisplayName || connection?.userName}`}
			</p>
		</li>
	) : null;
};

export const SocialMediaPlatformRow = inject(ImpersonationContextKey)(_SocialMediaPlatformRow);
